import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaRegEdit } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import styles from "./Styles/Client.module.css";
import { listClientsApi, createClientApi, deleteClientApi } from "../../Redux/Clients/Client.actions";
import { listMembersApi } from "../../Redux/Organizations/Org.actions";
import ConfigForm from "../common/ConfigForm";
import CollapsiblePanel from "../common/CollapsiblePanel";
import Tooltip from "../common/Tooltip";
import ConfirmModal from "../common/ConfirmModal";
import EmptyState from "../common/EmptyState";
import NeedOrganizationEmptyState from "../common/NeedOrganizationEmptyState";
import CopyIdButton from "../common/CopyIdButton";
import EditClientModal from "./EditClientModal";
import { isAdminOrOwner as computeIsAdminOrOwner } from "../common/permissions";
import { useI18n } from "../../i18n/I18nContext";
import { apiErrorMessage } from "../../i18n/translate";
import useCountries from "../common/useCountries";
import useCountryFields from "../common/useCountryFields";
import { identificationFieldConfig } from "../common/identificationFields";

const initialFields = {
  name: "",
  email: "",
  invoiceEmails: "",
  contactName: "",
  address: "",
  postalCode: "",
  city: "",
  country: "",
  vatNumber: "",
  vatRate: "",
  vatDischargeMotive: "",
  siren: "",
  siret: "",
  naf: "",
  mf: "",
  identificationNumber: "",
  purchaseOrder: "",
  hoursPerDay: "",
  dailyRate: "",
  sendReportsWithInvoice: false,
};

const Clients = () => {
  const { t, locale } = useI18n();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentOrgId } = useSelector((state) => state.organizations);
  const { clients } = useSelector((state) => state.clients);
  const { members } = useSelector((state) => state.organizations);
  const [fields, setFields] = useState(initialFields);
  const [error, setError] = useState("");
  const [editingClient, setEditingClient] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [search, setSearch] = useState("");

  const isAdminOrOwner = computeIsAdminOrOwner(user, members);
  const countries = useCountries();
  const identificationFields = useCountryFields(fields.country);

  const clientFormConfig = {
    name: "Client",
    fields: [
      { name: "name", type: "text", label: t("common.name"), required: true },
      { name: "email", type: "email", label: t("common.email") },
      { name: "invoiceEmails", type: "text", label: t("clients.invoiceEmails"), placeholder: t("clients.invoiceEmailsHint") },
      { name: "contactName", type: "text", label: t("clients.contactName") },
      { name: "address", type: "text", label: t("common.address") },
      { name: "postalCode", type: "text", label: t("common.postalCode") },
      { name: "city", type: "text", label: t("common.city") },
      {
        name: "country",
        type: "autocomplete",
        label: t("common.country"),
        placeholder: t("common.country"),
        required: true,
        options: countries.map((c) => ({ value: c.iso, label: c.name })),
      },
      { name: "vatRate", type: "number", label: t("clients.vatRateLabel"), step: "0.01" },
      { name: "vatDischargeMotive", type: "text", label: t("clients.vatDischargeMotive") },
      ...identificationFields.map((name) => identificationFieldConfig(name, t)),
      { name: "purchaseOrder", type: "text", label: t("clients.purchaseOrder") },
      { name: "hoursPerDay", type: "number", label: t("clients.hoursPerDay"), step: "0.01" },
      { name: "dailyRate", type: "number", label: t("clients.dailyRate"), step: "0.01", min: "0" },
      { name: "sendReportsWithInvoice", type: "checkbox", label: t("clients.sendReportsWithInvoice") },
    ],
  };

  useEffect(() => {
    if (currentOrgId) {
      dispatch(listClientsApi(currentOrgId, user.token));
      dispatch(listMembersApi(currentOrgId, user.token));
    }
  }, [dispatch, currentOrgId, user.token]);

  const setField = (key, value) => setFields({ ...fields, [key]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!currentOrgId) return;
    const payload = {
      ...fields,
      vatRate: fields.vatRate === "" ? undefined : Number(fields.vatRate),
      hoursPerDay: fields.hoursPerDay === "" ? undefined : Number(fields.hoursPerDay),
      dailyRate: fields.dailyRate === "" ? undefined : Number(fields.dailyRate),
    };
    try {
      await dispatch(createClientApi(currentOrgId, payload, user.token));
      setFields(initialFields);
    } catch (err) {
      setError(apiErrorMessage(err, locale));
    }
  };

  const handleDelete = async () => {
    const clientId = deletingClient.id;
    setDeletingClient(null);
    await dispatch(deleteClientApi(currentOrgId, clientId, user.token));
  };

  const visibleClients = clients
    .filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (!currentOrgId) {
    return <NeedOrganizationEmptyState body={t("organizations.selectOrCreateFirst")} />;
  }

  return (
    <div className={styles.main}>
      <h1 className="cw-title">{t("clients.title")}</h1>
      <CollapsiblePanel title={t("clients.createClient")}>
        <ConfigForm
          config={clientFormConfig}
          values={fields}
          onChange={setField}
          onSubmit={handleSubmit}
          submitLabel={t("common.add")}
          error={error}
        />
      </CollapsiblePanel>

      {clients.length === 0 && (
        <EmptyState title={t("clients.emptyTitle")} body={t("clients.emptyBody")} />
      )}

      {clients.length > 0 && (
        <div className={styles.filters}>
          <input
            className={`cw-input ${styles.search}`}
            type="text"
            placeholder={t("common.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      <ul className="cw-list">
        {visibleClients.map((client) => (
          <li className={`cw-list-item ${styles.clientRow}`} key={client.id}>
            <span>
              <strong>{client.name}</strong>
              {client.address && `, ${client.address}`}
              {client.postalCode && ` ${client.postalCode}`}
              {client.city && ` ${client.city}`}
              {client.country && ` ${client.country}`}
              {t("clients.vatAndHours", { rate: client.vatRate, hours: client.hoursPerDay })}
              {isAdminOrOwner && client.dailyRate
                ? t("clients.dailyRateSet", { rate: client.dailyRate })
                : ""}
            </span>
            <div className={styles.rowActions}>
              <CopyIdButton id={client.id} className={styles.iconBtn} />
              {isAdminOrOwner && (
                <Tooltip label={t("common.edit")}>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={() => setEditingClient(client)}
                  >
                    <FaRegEdit style={{ fontSize: "18px" }} />
                  </button>
                </Tooltip>
              )}
              {isAdminOrOwner && (
                <Tooltip label={t("common.delete")}>
                  <button
                    type="button"
                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                    onClick={() => setDeletingClient(client)}
                  >
                    <MdDeleteForever style={{ fontSize: "20px" }} />
                  </button>
                </Tooltip>
              )}
            </div>
          </li>
        ))}
      </ul>

      <EditClientModal
        show={!!editingClient}
        onClose={() => setEditingClient(null)}
        targetClient={editingClient}
        orgId={currentOrgId}
        token={user.token}
      />

      <ConfirmModal
        show={!!deletingClient}
        title={t("clients.deleteClientTitle")}
        body={deletingClient ? t("clients.deleteClientBody", { name: deletingClient.name }) : ""}
        confirmLabel={t("common.delete")}
        onConfirm={handleDelete}
        onCancel={() => setDeletingClient(null)}
      />
    </div>
  );
};

export default Clients;
